import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Plus,
  Users,
  CreditCard,
  Settings,
  MessageSquare,
  ShieldCheck,
  MoreHorizontal,
} from 'lucide-react';
import { cn, avatarText } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: { text: string; tone?: 'default' | 'danger' };
}

function NavItem({ to, icon, label, badge }: NavItemProps) {
  const location = useLocation();
  const isActive =
    location.pathname === to ||
    (to !== '/' && location.pathname.startsWith(to + '/')) ||
    (to === '/projects' && location.pathname.startsWith('/projects/'));

  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive: navIsActive }) =>
        cn(
          'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13.5px] font-medium transition-colors mb-0.5',
          (navIsActive || isActive) ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )
      }
    >
      <span className={cn('w-5 text-center inline-flex justify-center')}>{icon}</span>
      <span className="flex-1">{label}</span>
      {badge && (
        <span
          className={cn(
            'ml-auto rounded-full px-1.5 py-px text-[10px] font-bold',
            badge.tone === 'danger' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600',
            'group-[.active]:bg-white/20 group-[.active]:text-white',
          )}
        >
          {badge.text}
        </span>
      )}
    </NavLink>
  );
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
}

function SidebarSection({ title, children }: SidebarSectionProps) {
  return (
    <div className="px-3 pt-3.5 pb-1">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 px-2 pb-2">{title}</div>
      {children}
    </div>
  );
}

export function Sidebar() {
  const { user } = useAuth();

  return (
    <aside className="hidden md:flex flex-col w-60 h-screen sticky top-0 bg-white border-r border-slate-200 flex-shrink-0 overflow-y-auto scrollbar-thin">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-extrabold text-sm">
          漾
        </div>
        <div>
          <div className="font-bold text-[15px] leading-tight">漾丰國際</div>
          <div className="text-[11px] text-slate-500">業務自動化系統</div>
        </div>
      </div>

      <nav className="flex-1">
        <SidebarSection title="主控台">
          <NavItem to="/" icon={<LayoutDashboard size={15} />} label="Dashboard" />
        </SidebarSection>

        <SidebarSection title="專案">
          <NavItem to="/projects" icon={<FolderKanban size={15} />} label="專案列表" />
          <NavItem to="/projects/new" icon={<Plus size={15} />} label="新增專案" />
        </SidebarSection>

        <SidebarSection title="人員">
          <NavItem to="/organizers" icon={<Users size={15} />} label="整理師列表" />
          <NavItem to="/payroll" icon={<Users size={15} />} label="薪資彙整" />
        </SidebarSection>

        <SidebarSection title="金流">
          <NavItem to="/payments" icon={<CreditCard size={15} />} label="金流付款" />
        </SidebarSection>

        <SidebarSection title="回饋">
          <NavItem to="/feedback" icon={<MessageSquare size={15} />} label="回饋處理" />
        </SidebarSection>

        <SidebarSection title="設定">
          <NavItem to="/settings" icon={<Settings size={15} />} label="系統設定" />
          {user?.role === 'admin' && (
            <NavItem to="/access-requests" icon={<ShieldCheck size={15} />} label="LINE 登入審核" />
          )}
        </SidebarSection>
      </nav>

      {/* Footer (user) */}
      <div className="mt-auto px-4 py-3 border-t border-slate-100 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[13px]">
          {avatarText(user?.name)}
        </div>
        <div className="flex-1 leading-tight">
          <div className="text-[13px] font-semibold">{user?.name || '未登入'}</div>
          <div className="text-[11px] text-slate-400">
            {user?.role === 'admin' ? '負責人 · admin' : '業務 · staff'}
          </div>
        </div>
        <MoreHorizontal size={16} className="text-slate-400 cursor-pointer" />
      </div>
    </aside>
  );
}
